// components/FBBillingInvoices.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Badge,
} from '@mui/material';
import {
  Search,
  CheckCircle,
  Refresh,
  Receipt,
  Print,
  Download,
  CalendarToday,
  Restaurant,
  RoomService,
  TakeoutDining,
  DeliveryDining,
  AttachMoney,
  Person,
  Visibility,
  FileCopy,
  Email,
  Share,
  Cancel,
  Paid,
  Pending,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../../../components/sidebars/sidebar_f&b';
import TopNav from '../../../../components/TopNav_hotelStaff';

// Styled Components
const InvoiceCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return theme.palette.success.main;
      case 'paid': return theme.palette.success.main;
      case 'pending': return theme.palette.warning.main;
      case 'confirmed': return theme.palette.info.main;
      case 'preparing': return theme.palette.primary.main;
      case 'ready': return theme.palette.success.light;
      case 'served': return theme.palette.success.dark;
      case 'cancelled': return theme.palette.error.main;
      default: return theme.palette.grey[500];
    }
  };

  return {
    backgroundColor: getStatusColor(status),
    color: 'white',
    fontWeight: 'bold',
    fontSize: '12px',
  };
});

const PaymentBadge = styled(Chip)(({ theme, paid }) => ({
  backgroundColor: paid ? theme.palette.success.light : theme.palette.warning.light,
  color: paid ? theme.palette.success.dark : theme.palette.warning.dark,
  border: `1px solid ${paid ? theme.palette.success.main : theme.palette.warning.main}`,
  fontSize: '11px',
  height: '24px',
  fontWeight: 'bold',
}));

const StaffBadge = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.info.light,
  color: theme.palette.info.dark,
  border: `1px solid ${theme.palette.info.main}`,
  fontSize: '11px',
  height: '24px',
}));

const OrderTypeIcon = ({ type }) => {
  const icons = {
    dine_in: <Restaurant />,
    room_service: <RoomService />,
    takeaway: <TakeoutDining />,
    delivery: <DeliveryDining />,
  };
  
  return icons[type] || <Receipt />;
};

const FBBillingInvoices = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:768px)');
  
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const [orderTypeFilter, setOrderTypeFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [staffFilter, setStaffFilter] = useState('all');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const HOTEL_NAME = process.env.REACT_APP_HOTEL_NAME || 'Hotel';
  const HOTEL_ADDRESS = process.env.REACT_APP_HOTEL_ADDRESS || '';
  const HOTEL_PHONE = process.env.REACT_APP_HOTEL_PHONE || '';
  const TAX_RATE = parseFloat(process.env.REACT_APP_HOTEL_TAX) || 7.5;
  const SERVICE_CHARGE_RATE = parseFloat(process.env.REACT_APP_HOTEL_SERVICE_CHARGE) || 10;

  const getHotelCode = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
      return storedProfile.hotelcode || storedProfile.hotel_code || '';
    } catch (error) {
      console.error('Error getting hotel code:', error);
      return '';
    }
  };

  const getCurrentStaffId = () => {
    const storedProfile = JSON.parse(localStorage.getItem('userProfile')) || {};
    return storedProfile.staff_id || storedProfile.id || null;
  };

  // Load all data
  const loadAllData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const hotelCode = getHotelCode();
      if (!hotelCode) {
        throw new Error('Hotel code not found');
      }

      // Load staff members
      let staffData = [];
      try {
        const staffResponse = await fetch(
          `${API_BASE_URL}/hotel/staff.php?hotel_code=${hotelCode}`
        );
        const staffResult = await staffResponse.json();
        if (staffResult.success) {
          staffData = staffResult.staff || [];
        }
      } catch (staffError) {
        console.warn('Could not load staff members:', staffError);
      }

      setStaffMembers(staffData);

      // Load orders
      const ordersResponse = await fetch(
        `${API_BASE_URL}/hotel/F&B/orders.php?hotel_code=${hotelCode}`
      );
      const ordersData = await ordersResponse.json();

      if (ordersData.success) {
        // Enhance orders with staff information and payment status
        const enhancedOrders = ordersData.orders.map(order => {
          const createdByStaff = staffData.find(staff => 
            staff.id == order.created_by || staff.staff_id == order.created_by
          );
          
          const servedByStaff = staffData.find(staff => 
            staff.id == order.served_by || staff.staff_id == order.served_by
          );

          // Determine payment status (simulated - you might have actual payment data)
          const isPaid = order.status === 'completed' || order.status === 'served';
          const paymentStatus = isPaid ? 'paid' : 'pending';

          return {
            ...order,
            created_by_staff: createdByStaff ? {
              id: createdByStaff.id || createdByStaff.staff_id,
              name: `${createdByStaff.first_name || ''} ${createdByStaff.other_names || ''}`.trim()
            } : { id: order.created_by, name: `Staff ${order.created_by}` },
            served_by_staff: servedByStaff ? {
              id: servedByStaff.id || servedByStaff.staff_id,
              name: `${servedByStaff.first_name || ''} ${servedByStaff.other_names || ''}`.trim()
            } : (order.served_by ? { id: order.served_by, name: `Staff ${order.served_by}` } : null),
            payment_status: paymentStatus,
            is_paid: isPaid,
            // Calculate item totals
            total_items_count: order.items ? order.items.reduce((sum, item) => sum + (item.quantity || 1), 0) : 0,
            subtotal: order.items ? order.items.reduce((sum, item) => sum + ((item.quantity || 1) * (item.unit_price || 0)), 0) : 0,
          };
        });

        setOrders(enhancedOrders);
        console.log('Billing data loaded:', enhancedOrders.length, 'orders');
      } else {
        throw new Error(ordersData.error || 'Failed to load orders');
      }
    } catch (err) {
      console.error('Error loading billing data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadAllData();
  }, []);

  // Apply filters
  useEffect(() => {
    if (orders.length === 0) return;

    let filtered = [...orders];

    // Date range filter
    filtered = filtered.filter(order => {
      const orderDate = new Date(order.created_at).toISOString().split('T')[0];
      return orderDate >= dateRange.startDate && orderDate <= dateRange.endDate;
    });

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(order =>
        order.order_number?.toLowerCase().includes(query) ||
        order.table_number?.toLowerCase().includes(query) ||
        order.room_number?.toLowerCase().includes(query) ||
        order.first_name?.toLowerCase().includes(query) ||
        order.last_name?.toLowerCase().includes(query) ||
        order.created_by_staff?.name?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Order type filter
    if (orderTypeFilter !== 'all') {
      filtered = filtered.filter(order => order.order_type === orderTypeFilter);
    }

    // Payment filter
    if (paymentFilter !== 'all') {
      filtered = filtered.filter(order => order.payment_status === paymentFilter);
    }

    // Staff filter
    if (staffFilter !== 'all') {
      filtered = filtered.filter(order => order.created_by == staffFilter);
    }

    setFilteredOrders(filtered);
    setPage(0);
  }, [orders, searchQuery, dateRange, statusFilter, orderTypeFilter, paymentFilter, staffFilter]);

  // Calculate summary statistics
  const calculateSummary = () => {
    const totalRevenue = filteredOrders
      .filter(order => order.status !== 'cancelled')
      .reduce((sum, order) => sum + (parseFloat(order.final_amount) || 0), 0);

    const paidRevenue = filteredOrders
      .filter(order => order.is_paid)
      .reduce((sum, order) => sum + (parseFloat(order.final_amount) || 0), 0);

    const pendingRevenue = filteredOrders
      .filter(order => !order.is_paid && order.status !== 'cancelled')
      .reduce((sum, order) => sum + (parseFloat(order.final_amount) || 0), 0);

    const paidOrders = filteredOrders.filter(order => order.is_paid).length;
    const pendingOrders = filteredOrders.filter(order => !order.is_paid && order.status !== 'cancelled').length;
    const cancelledOrders = filteredOrders.filter(order => order.status === 'cancelled').length;

    return {
      totalOrders: filteredOrders.length,
      totalRevenue,
      paidRevenue,
      pendingRevenue,
      paidOrders,
      pendingOrders,
      cancelledOrders,
      collectionRate: totalRevenue > 0 ? (paidRevenue / totalRevenue * 100) : 0,
    };
  };

  const summary = calculateSummary();

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get current page orders
  const currentOrders = filteredOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Print invoice
  const printInvoice = (order) => {
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    const hotelCode = getHotelCode();
    
    const subtotal = parseFloat(order.total_amount) || 0;
    const taxAmount = parseFloat(order.tax_amount) || (subtotal * TAX_RATE / 100);
    const serviceCharge = parseFloat(order.service_charge) || (subtotal * SERVICE_CHARGE_RATE / 100);
    const totalAmount = parseFloat(order.final_amount) || (subtotal + taxAmount + serviceCharge);

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice - ${order.order_number}</title>
        <style>
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.4;
            margin: 0;
            padding: 15px;
            width: 80mm;
            background: white;
          }
          .header {
            text-align: center;
            border-bottom: 2px dashed #000;
            padding-bottom: 10px;
            margin-bottom: 10px;
          }
          .hotel-name {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          .hotel-info {
            font-size: 10px;
            margin-bottom: 5px;
          }
          .invoice-title {
            text-align: center;
            font-size: 14px;
            font-weight: bold;
            margin: 10px 0;
          }
          .order-info {
            margin-bottom: 10px;
            padding: 8px;
            background: #f5f5f5;
            border-radius: 4px;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 3px;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 10px 0;
          }
          .items-table th {
            border-bottom: 1px solid #000;
            padding: 5px;
            text-align: left;
          }
          .items-table td {
            padding: 5px;
            border-bottom: 1px dashed #ddd;
          }
          .items-table .qty {
            width: 20px;
            text-align: center;
          }
          .items-table .price {
            text-align: right;
            width: 60px;
          }
          .totals {
            margin-top: 10px;
            border-top: 1px solid #000;
            padding-top: 10px;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 3px;
          }
          .grand-total {
            font-weight: bold;
            font-size: 14px;
            border-top: 2px solid #000;
            padding-top: 5px;
            margin-top: 5px;
          }
          .payment-status {
            text-align: center;
            margin: 10px 0;
            padding: 5px;
            background: ${order.is_paid ? '#d4edda' : '#fff3cd'};
            border: 1px solid ${order.is_paid ? '#c3e6cb' : '#ffeaa7'};
            border-radius: 4px;
            font-weight: bold;
          }
          .footer {
            text-align: center;
            margin-top: 15px;
            font-size: 10px;
            border-top: 1px dashed #000;
            padding-top: 10px;
          }
          .thank-you {
            text-align: center;
            margin: 10px 0;
            font-weight: bold;
          }
          @media print {
            body { margin: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="hotel-name">${HOTEL_NAME}</div>
          <div class="hotel-info">${HOTEL_ADDRESS}</div>
          <div class="hotel-info">Tel: ${HOTEL_PHONE}</div>
          <div class="hotel-info">Hotel Code: ${hotelCode}</div>
        </div>
        
        <div class="invoice-title">TAX INVOICE</div>
        
        <div class="order-info">
          <div class="info-row">
            <span>Invoice #:</span>
            <span><strong>${order.order_number}</strong></span>
          </div>
          <div class="info-row">
            <span>Date:</span>
            <span>${new Date(order.created_at).toLocaleDateString()}</span>
          </div>
          <div class="info-row">
            <span>Time:</span>
            <span>${new Date(order.created_at).toLocaleTimeString()}</span>
          </div>
          <div class="info-row">
            <span>Order Type:</span>
            <span>${order.order_type.replace('_', ' ').toUpperCase()}</span>
          </div>
          ${order.table_number ? `
            <div class="info-row">
              <span>Table:</span>
              <span>${order.table_number}</span>
            </div>
          ` : ''}
          ${order.room_number ? `
            <div class="info-row">
              <span>Room:</span>
              <span>${order.room_number}</span>
            </div>
          ` : ''}
          ${order.first_name ? `
            <div class="info-row">
              <span>Customer:</span>
              <span>${order.first_name} ${order.last_name}</span>
            </div>
          ` : ''}
        </div>

        <table class="items-table">
          <thead>
            <tr>
              <th class="qty">Qty</th>
              <th>Item</th>
              <th class="price">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${order.items?.map(item => `
              <tr>
                <td class="qty">${item.quantity}</td>
                <td>${item.menu_item_name}</td>
                <td class="price">₦${(item.quantity * item.unit_price).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="totals">
          <div class="total-row">
            <span>Subtotal:</span>
            <span>₦${subtotal.toFixed(2)}</span>
          </div>
          ${taxAmount > 0 ? `
            <div class="total-row">
              <span>Tax (${TAX_RATE}%):</span>
              <span>₦${taxAmount.toFixed(2)}</span>
            </div>
          ` : ''}
          ${serviceCharge > 0 ? `
            <div class="total-row">
              <span>Service Charge (${SERVICE_CHARGE_RATE}%):</span>
              <span>₦${serviceCharge.toFixed(2)}</span>
            </div>
          ` : ''}
          <div class="total-row grand-total">
            <span>TOTAL:</span>
            <span>₦${totalAmount.toFixed(2)}</span>
          </div>
        </div>

        <div class="payment-status">
          PAYMENT STATUS: ${order.is_paid ? 'PAID' : 'PENDING'}
        </div>

        ${order.special_instructions ? `
          <div style="margin: 10px 0; font-style: italic; font-size: 10px;">
            <strong>Notes:</strong> ${order.special_instructions}
          </div>
        ` : ''}

        <div class="thank-you">Thank you for your business!</div>
        
        <div class="footer">
          Generated: ${new Date().toLocaleString()}<br>
          ${HOTEL_NAME} F&B System
        </div>
        
        <div class="no-print" style="text-align: center; margin-top: 20px;">
          <button onclick="window.print()" style="padding: 10px 20px; background: #1976d2; color: white; border: none; border-radius: 4px; cursor: pointer;">
            Print Invoice
          </button>
          <button onclick="window.close()" style="padding: 10px 20px; background: #6c757d; color: white; border: none; border-radius: 4px; cursor: pointer; margin-left: 10px;">
            Close
          </button>
        </div>
        
        <script>
          window.onload = function() {
            // Auto-print after a short delay
            setTimeout(function() {
              window.print();
            }, 500);
          }
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Bulk print selected invoices
  const bulkPrintInvoices = () => {
    filteredOrders.forEach((order, index) => {
      setTimeout(() => {
        printInvoice(order);
      }, index * 1000); // Stagger prints by 1 second
    });
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Order Number', 'Date', 'Time', 'Type', 'Table/Room', 'Customer', 'Subtotal', 'Tax', 'Service Charge', 'Total Amount', 'Payment Status', 'Order Status', 'Created By'];
    const csvData = filteredOrders.map(order => [
      order.order_number,
      new Date(order.created_at).toLocaleDateString(),
      new Date(order.created_at).toLocaleTimeString(),
      order.order_type,
      order.table_number || order.room_number || '-',
      order.first_name ? `${order.first_name} ${order.last_name}` : 'Walk-in',
      `₦${(parseFloat(order.total_amount) || 0).toFixed(2)}`,
      `₦${(parseFloat(order.tax_amount) || 0).toFixed(2)}`,
      `₦${(parseFloat(order.service_charge) || 0).toFixed(2)}`,
      `₦${(parseFloat(order.final_amount) || 0).toFixed(2)}`,
      order.payment_status.toUpperCase(),
      order.status.toUpperCase(),
      order.created_by_staff?.name || `Staff ${order.created_by}`
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fb-invoices-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          mt: 0,
          maxWidth: '1400px',
          mx: 'auto',
        }}
      >
        <TopNav
          onLogout={() => navigate('/HotelAdmin')}
          onNotificationClick={() => alert('Notifications')}
          toggleSidebar={toggleSidebar}
        />

        {/* Header */}
        <Paper elevation={0} sx={{ p: 3, mt: 3, backgroundColor: 'primary.main', color: 'white', borderRadius: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            🧾 F&B Billing & Invoices
          </Typography>
          <Typography variant="subtitle1">
            Manage invoices, print receipts, and track payments
          </Typography>
        </Paper>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mt: 2, mb: 3 }}>
          {[
            {
              title: 'Total Invoices',
              value: summary.totalOrders,
              icon: <Receipt />,
              color: 'primary',
              description: 'All orders'
            },
            {
              title: 'Total Revenue',
              value: `₦${summary.totalRevenue.toFixed(2)}`,
              icon: <AttachMoney />,
              color: 'success',
              description: 'Gross amount'
            },
            {
              title: 'Paid Amount',
              value: `₦${summary.paidRevenue.toFixed(2)}`,
              icon: <Paid />,
              color: 'success',
              description: 'Collected'
            },
            {
              title: 'Pending Amount',
              value: `₦${summary.pendingRevenue.toFixed(2)}`,
              icon: <Pending />,
              color: 'warning',
              description: 'Outstanding'
            },
            {
              title: 'Paid Invoices',
              value: summary.paidOrders,
              icon: <CheckCircle />,
              color: 'success',
              description: 'Settled'
            },
            {
              title: 'Collection Rate',
              value: `${summary.collectionRate.toFixed(1)}%`,
              icon: <AttachMoney />,
              color: 'info',
              description: 'Efficiency'
            },
          ].map((stat, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
              <InvoiceCard>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" fontWeight="bold" color={`${stat.color}.main`}>
                        {stat.value}
                      </Typography>
                      <Typography variant="h6" sx={{ fontSize: '0.9rem', mb: 0.5 }}>
                        {stat.title}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {stat.description}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: `${stat.color}.light`,
                        borderRadius: '50%',
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {React.cloneElement(stat.icon, {
                        sx: { color: `${stat.color}.main`, fontSize: 24 }
                      })}
                    </Box>
                  </Box>
                </CardContent>
              </InvoiceCard>
            </Grid>
          ))}
        </Grid>

        {/* Filters Section */}
        <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Search Invoices"
                placeholder="Order #, table, customer..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Order Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Order Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="preparing">Preparing</MenuItem>
                  <MenuItem value="ready">Ready</MenuItem>
                  <MenuItem value="served">Served</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Payment Status</InputLabel>
                <Select
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                  label="Payment Status"
                >
                  <MenuItem value="all">All Payments</MenuItem>
                  <MenuItem value="paid">Paid</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Order Type</InputLabel>
                <Select
                  value={orderTypeFilter}
                  onChange={(e) => setOrderTypeFilter(e.target.value)}
                  label="Order Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="dine_in">Dine In</MenuItem>
                  <MenuItem value="room_service">Room Service</MenuItem>
                  <MenuItem value="takeaway">Takeaway</MenuItem>
                  <MenuItem value="delivery">Delivery</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Staff</InputLabel>
                <Select
                  value={staffFilter}
                  onChange={(e) => setStaffFilter(e.target.value)}
                  label="Staff"
                >
                  <MenuItem value="all">All Staff</MenuItem>
                  {staffMembers.map(staff => (
                    <MenuItem key={staff.id || staff.staff_id} value={staff.id || staff.staff_id}>
                      {`${staff.first_name || ''} ${staff.other_names || ''}`.trim() || `Staff ${staff.id || staff.staff_id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Tooltip title="Refresh Data">
                  <IconButton onClick={loadAllData} color="primary">
                    <Refresh />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Print All Filtered">
                  <IconButton onClick={bulkPrintInvoices} color="primary">
                    <Print />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Export CSV">
                  <IconButton onClick={exportToCSV} color="primary">
                    <Download />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            <Button onClick={loadAllData} sx={{ ml: 2 }} size="small">
              Retry
            </Button>
          </Alert>
        )}

        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading billing data...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Invoices Table */}
            <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Invoice #</strong></TableCell>
                      <TableCell><strong>Date & Time</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Table/Room</strong></TableCell>
                      <TableCell><strong>Customer</strong></TableCell>
                      <TableCell><strong>Items</strong></TableCell>
                      <TableCell><strong>Order Status</strong></TableCell>
                      <TableCell><strong>Payment</strong></TableCell>
                      <TableCell align="right"><strong>Amount</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentOrders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                          <Typography variant="h6" color="textSecondary">
                            No invoices found for the selected filters
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            Try adjusting your search criteria or date range
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      currentOrders.map((order) => (
                        <TableRow 
                          key={order.id}
                          sx={{ 
                            '&:hover': { backgroundColor: 'action.hover' },
                            backgroundColor: order.status === 'cancelled' ? 'error.light' : 'inherit'
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {order.order_number}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2">
                                {new Date(order.created_at).toLocaleDateString()}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {new Date(order.created_at).toLocaleTimeString()}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <OrderTypeIcon type={order.order_type} />
                              <Typography variant="body2">
                                {order.order_type.replace('_', ' ').toUpperCase()}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {order.table_number || order.room_number || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {order.first_name ? `${order.first_name} ${order.last_name}` : 'Walk-in'}
                            </Typography>
                            {order.phone && (
                              <Typography variant="caption" color="textSecondary">
                                {order.phone}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {order.total_items_count} items
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {order.items?.slice(0, 2).map(item => item.menu_item_name).join(', ')}
                              {order.items && order.items.length > 2 && '...'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusChip 
                              label={order.status.toUpperCase()} 
                              status={order.status}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <PaymentBadge 
                              label={order.payment_status.toUpperCase()}
                              paid={order.is_paid}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body1" fontWeight="bold" color="primary">
                              ₦{parseFloat(order.final_amount || 0).toFixed(2)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              Tax: ₦{(parseFloat(order.tax_amount) || 0).toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Print Invoice">
                                <IconButton 
                                  size="small" 
                                  color="primary"
                                  onClick={() => printInvoice(order)}
                                >
                                  <Print />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="View Details">
                                <IconButton 
                                  size="small" 
                                  color="info"
                                  onClick={() => setSelectedOrder(order)}
                                >
                                  <Visibility />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Mark as Paid">
                                <IconButton 
                                  size="small" 
                                  color="success"
                                  disabled={order.is_paid}
                                >
                                  <Paid />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredOrders.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
              />
            </Paper>

            {/* Summary Footer */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                Showing {Math.min(rowsPerPage, currentOrders.length)} of {filteredOrders.length} invoices
                {dateRange.startDate !== dateRange.endDate && 
                  ` from ${dateRange.startDate} to ${dateRange.endDate}`
                }
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Last updated: {new Date().toLocaleString()}
              </Typography>
            </Box>
          </>
        )}

        {/* Order Details Dialog */}
        <Dialog 
          open={!!selectedOrder} 
          onClose={() => setSelectedOrder(null)} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>
            Invoice Details - {selectedOrder?.order_number}
          </DialogTitle>
          <DialogContent>
            {selectedOrder && (
              <Box>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>Order Information</Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText primary="Order Number" secondary={selectedOrder.order_number} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Date & Time" secondary={new Date(selectedOrder.created_at).toLocaleString()} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Order Type" secondary={selectedOrder.order_type.replace('_', ' ').toUpperCase()} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Table/Room" secondary={selectedOrder.table_number || selectedOrder.room_number || 'N/A'} />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>Customer & Payment</Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText 
                          primary="Customer" 
                          secondary={selectedOrder.first_name ? `${selectedOrder.first_name} ${selectedOrder.last_name}` : 'Walk-in Customer'} 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Order Status" secondary={
                          <StatusChip 
                            label={selectedOrder.status.toUpperCase()} 
                            status={selectedOrder.status}
                            size="small"
                          />
                        } />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Payment Status" secondary={
                          <PaymentBadge 
                            label={selectedOrder.payment_status.toUpperCase()}
                            paid={selectedOrder.is_paid}
                            size="small"
                          />
                        } />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Created By" secondary={selectedOrder.created_by_staff?.name || `Staff ${selectedOrder.created_by}`} />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>Order Items</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell align="center">Qty</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedOrder.items?.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.menu_item_name}</TableCell>
                          <TableCell align="center">{item.quantity}</TableCell>
                          <TableCell align="right">₦{parseFloat(item.unit_price).toFixed(2)}</TableCell>
                          <TableCell align="right">₦{(item.quantity * item.unit_price).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2">Subtotal:</Typography>
                    </Grid>
                    <Grid item xs={6} textAlign="right">
                      <Typography variant="body2">₦{(parseFloat(selectedOrder.total_amount) || 0).toFixed(2)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">Tax ({TAX_RATE}%):</Typography>
                    </Grid>
                    <Grid item xs={6} textAlign="right">
                      <Typography variant="body2">₦{(parseFloat(selectedOrder.tax_amount) || 0).toFixed(2)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">Service Charge ({SERVICE_CHARGE_RATE}%):</Typography>
                    </Grid>
                    <Grid item xs={6} textAlign="right">
                      <Typography variant="body2">₦{(parseFloat(selectedOrder.service_charge) || 0).toFixed(2)}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="h6">Total:</Typography>
                    </Grid>
                    <Grid item xs={6} textAlign="right">
                      <Typography variant="h6" color="primary">
                        ₦{parseFloat(selectedOrder.final_amount || 0).toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {selectedOrder.special_instructions && (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: 'warning.light', borderRadius: 1 }}>
                    <Typography variant="subtitle2">Special Instructions:</Typography>
                    <Typography variant="body2">{selectedOrder.special_instructions}</Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSelectedOrder(null)}>Close</Button>
            <Button 
              variant="contained" 
              startIcon={<Print />}
              onClick={() => {
                printInvoice(selectedOrder);
                setSelectedOrder(null);
              }}
            >
              Print Invoice
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default FBBillingInvoices;